<template>
  <div :class="$style.container">
    <h1 :class="$style.title">{{ title }}</h1>

    <div>
      <FileInput
        accept="image/*"
        @input="onPictureFileChange"
        :class="$style.file"
      >
        <div :class="[$style.picture, { [$style.pictureError]: $v.pictureFile.$error }]">
          <img
            v-if="pictureSrc"
            :src="pictureSrc"
            alt="profile picture"
            :class="$style.image"
          >
          <BaseIcon name="pencil-alt" :class="[$style.pencilIcon, $style.pencilIconPicture]" />
        </div>
      </FileInput>
      <FileInput
        accept="image/*"
        @input="onBackgroundFileChange"
        :class="$style.file"
      >
        <div :class="$style.background">
          <img
            v-if="backgroundSrc"
            :src="backgroundSrc"
            alt="profile picture"
            :class="$style.image"
          >
            <BaseIcon name="pencil-alt" :class="[$style.pencilIcon, $style.pencilIconBackground]" />
        </div>
      </FileInput>
    </div>
    <BaseInput
      v-model="firstName"
      :label="$t('socialProfile.firstName')"
      :invalid="$v.firstName.$error"
      :error="firstNameError"
      @blur="$v.firstName.$touch"
    />
    <BaseInput
      v-model="lastName"
      :label="$t('socialProfile.lastName')"
      :invalid="$v.lastName.$error"
      :error="lastNameError"
      @blur="$v.lastName.$touch"
    />
    <BaseInput
      v-model="nickname"
      :label="$t('socialProfile.nickname')"
      :invalid="$v.nickname.$error"
      :error="nicknameError"
      @blur="$v.nickname.$touch"
      @input="changeInputUsername"
    />
     <BaseSelect
      v-model="category"
      :label="$t('category')"
      :placeholder="$t('chooseCategory')"
      :options="categories"
      trackBy="value"
      optionLabel="label"
    />
     <BaseInput
      v-model="textLocation"
      :label="$t('socialProfile.textLocation')"
      :invalid="$v.textLocation.$error"
      :error="textLocationError"
      @blur="$v.textLocation.$touch"
    />

    <div :class="$style.extended">{{ $t('socialProfile.extendedSettings') }}</div>

    <div :class="$style.extendedBlock">
      <div :class="$style.label">{{ $t('socialProfile.showSocialSpace') }}</div>
      <BaseSwitch v-model="isActive" :class="$style.switchMargin">{{ $t('socialProfile.isActive') }}</BaseSwitch>
      <BaseSwitch v-model="isPublic">{{ $t('socialProfile.isPublic') }}</BaseSwitch>
    </div>
    <div :class="$style.extendedBlock">
      <div :class="$style.label">{{ $t('socialProfile.profileViewOptions') }}</div>
      <BaseSwitch v-model="showFollowers" :class="$style.switchMargin">{{ $t('socialProfile.showFollowers') }}</BaseSwitch>
      <BaseSwitch v-model="isChatActive">{{ $t('socialProfile.isChatActive') }}</BaseSwitch>
    </div>

    <p v-if="$v.pictureFile.$error" class="form--error">{{ $t('socialProfile.pictureError') }}</p>

    <BaseButton
      oval
      :loading="loading || checkNicknameLoading"
      @click="onSubmit"
      :class="$style.button"
    >{{ buttonText }}</BaseButton>
  </div>
</template>

<script>
import { required, maxLength, helpers } from 'vuelidate/lib/validators'
import { mapActions } from 'vuex'
import FileInput from '@/components/FileInput'
import imageCompression from 'browser-image-compression'
import api from '@/api'
import { debounce } from 'lodash'

const alphaNumHyphenUnderscoreValidator = helpers.regex('alphaNumAndDot', /^[-a-zA-Z0-9_]+$/i)

const NAMES_MAX_LENGTH = 20
const NICKNAME_MAX_LENGTH = 20
const LOCATION_MAX_LENGTH = 50

export default {
  name: 'social-create-profile',
  components: {
    FileInput
  },
  data () {
    return {
      pictureSrc: null,
      backgroundSrc: null,
      firstName: '',
      lastName: '',
      nickname: '',
      textLocation: '',
      category: {
        label: this.$t('socialProfile.categoryIndividual'),
        value: 'Individual'
      },
      isActive: false,
      isPublic: false,
      showFollowers: true,
      isChatActive: true,
      loading: false,
      pictureFile: null,
      backgroundFile: null,
      errorUniqNickname: false,
      checkNicknameLoading: false
    }
  },
  computed: {
    title () {
      return this.$t('socialProfile.createTitle')
    },
    buttonText () {
      return this.$t('buttonCreate')
    },
    categories () {
      return [
        {
          label: this.$t('socialProfile.categoryIndividual'),
          value: 'Individual'
        },
        {
          label: this.$t('socialProfile.categoryCompany'),
          value: 'Company'
        }
      ]
    },
    firstNameError () {
      if (!this.$v.firstName.maxLength) {
        return `${this.$t('errorMaxLength_begin')} ${NAMES_MAX_LENGTH} ${this.$t('errorLength_end')}`
      }
      return this.$t('errorEmptyField')
    },
    lastNameError () {
      if (!this.$v.lastName.maxLength) {
        return `${this.$t('errorMaxLength_begin')} ${NAMES_MAX_LENGTH} ${this.$t('errorLength_end')}`
      }
      return ''
    },
    nicknameError () {
      if (!this.$v.nickname.required) {
        return this.$t('errorEmptyField')
      } else if (!this.$v.nickname.maxLength) {
        return `${this.$t('errorMaxLength_begin')} ${NICKNAME_MAX_LENGTH} ${this.$t('errorLength_end')}`
      } else if (!this.$v.nickname.alphaNumHyphenUnderscoreValidator) {
        return this.$t('errorAlphanumericDashUnderscore') // 'a-zA-Z0-9_-'
      } else if (!this.$v.nickname.uniq) {
        return this.$t('socialProfile.errorNicknameExists')
      }
      return ''
    },
    textLocationError () {
      if (!this.$v.textLocation.maxLength) {
        return `${this.$t('errorMaxLength_begin')} ${LOCATION_MAX_LENGTH} ${this.$t('errorLength_end')}`
      }
      return this.$t('errorEmptyField')
    }
  },
  validations: {
    firstName: { required, maxLength: maxLength(NAMES_MAX_LENGTH) },
    lastName: { maxLength: maxLength(NAMES_MAX_LENGTH) },
    nickname: {
      required,
      maxLength: maxLength(NICKNAME_MAX_LENGTH),
      alphaNumHyphenUnderscoreValidator,
      uniq () {
        return !this.errorUniqNickname
      }
    },
    textLocation: { required, maxLength: maxLength(LOCATION_MAX_LENGTH) },
    pictureFile: { required }
  },
  methods: {
    ...mapActions({
      createProfile: 'entities/socialProfiles/createProfile',
      checkUsername: 'entities/socialProfiles/checkUsername'
    }),
    changeInputUsername () {
      this.nickname = this.nickname.toLowerCase()
      if (this.nickname.length) {
        this.checkNicknameLoading = true
        this.changeValidationUserName()
      }
    },
    changeValidationUserName: debounce(function () {
      this.checkUsername(this.nickname).then((response) => {
        this.errorUniqNickname = response.info !== 'Nickname is unique'
      }).finally(() => {
        this.checkNicknameLoading = false
        this.$v.nickname.$touch()
      })
    }, 500),
    async onSubmit () {
      this.errorUniqNickname = false
      this.$v.$touch()
      if (this.loading || this.$v.$invalid) return
      const data = {
        firstName: this.firstName,
        lastName: this.lastName,
        nickname: this.nickname,
        textLocation: this.textLocation,
        category: this.category.value,
        isActive: this.isActive,
        isPublic: this.isPublic,
        showFollowers: this.showFollowers,
        isChatActive: this.isChatActive,
        picture: this.pictureFile
      }
      const location = await api.location.coordinatesByAddress(this.textLocation)
      data.location = JSON.stringify(location)
      if (this.backgroundFile) {
        data.background = this.backgroundFile
      }
      this.loading = true
      try {
        await this.createProfile(data)
        this.$router.push({ name: 'social', params: { profileNickname: this.nickname } })
      } catch (e) {
        const { nickname } = e.response.data
        if (nickname) {
          this.errorUniqNickname = true
        }
      } finally {
        this.loading = false
      }
    },
    async onPictureFileChange (file) {
      this.$modal.show('cropper', {
        aspectRatio: 1,
        file,
        onDone: async canvas => {
          this.pictureSrc = canvas.toDataURL('image/jpeg')
          const blob = await imageCompression.getFilefromDataUrl(this.pictureSrc, 'social-profile-picture')
          const compressedBlob = await imageCompression(blob, {
            maxSizeMB: 1
          })
          this.pictureFile = new File([compressedBlob], `${compressedBlob.name}.${compressedBlob.type.split('/')[1]}`)
        }
      })
    },
    async onBackgroundFileChange (file) {
      this.$modal.show('cropper', {
        aspectRatio: 16 / 9,
        file,
        onDone: async canvas => {
          this.backgroundSrc = canvas.toDataURL('image/jpeg')
          const blob = await imageCompression.getFilefromDataUrl(this.backgroundSrc, 'social-profile-background')
          const compressedBlob = await imageCompression(blob, {
            maxSizeMB: 1
          })
          this.backgroundFile = new File([compressedBlob], `${compressedBlob.name}.${compressedBlob.type.split('/')[1]}`)
        }
      })
    }
  }
}
</script>

<style module lang="scss">
.container {
  margin: 20px 0;
  padding: 0 25px;
  overflow-y: scroll;
  .button {
    margin: 0 auto;
  }
}
.title {
  font-size: 18px;
  font-weight: 500;
  color: $text-success;
  margin: 0;
  margin-bottom: 16px;
  text-align: center;
}
.picture,
.background {
  background-color: $main-bg-light;
  border: 4px solid $border-color;
  overflow: hidden;
  position: relative;
  margin: 0 auto;
  transition: all 0.2s;
  &:hover {
    border-color: $text-mid;
    .pencilIcon {
      color: $text-mid;
    }
  }
}
.picture {
  width: 110px;
  height: 110px;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  &Error {
    border-color: $text-error;
  }
}
.background {
  width: 240px;
  height: 110px;
  border-radius: 5px;
  top: -65px;
}
.image {
  width: 100%;
}
.pencilIcon {
  font-size: 16px;
  color: $text-light;
  position: absolute;
  transition: all 0.7s;
}
.pencilIconPicture {
  position: absolute;
  top: 8px;
}
.pencilIconBackground {
  position: absolute;
  bottom: 8px;
  right: 8px;
}
.extended {
  width: 100%;
  text-align: center;
  padding: 10px 0;
  background-color: rgba($main-bg-colored, 0.15);
  text-transform: uppercase;
  margin-bottom: 12px;
}
.extendedBlock {
  padding: 12px 0;
}
.label {
  color: $text-colored;
  font-weight: 500;
  font-size: 12px;
  margin-bottom: 4px;
}
.switchMargin {
  margin-bottom: 2px;
}
</style>
